import React, { useContext, useState, useEffect } from 'react';
import { Search, AlertCircle, Loader2, Eye, Filter, X, ChevronDown, Users, Building2, UserX, UserCheck, AlertTriangle, PenSquareIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import superAdminServices from '../../../services/superAdminServices';
import Pagination from '../../../components/Pagination';

export default function AllUsers() {
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [showFiltersSection, setShowFiltersSection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });

  const [filters, setFilters] = useState({
    searchTerm: '',
    organization: '',
    status: '',
    sortBy: 'newest'
  });

  // Deactivation popup state
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [deactivationLoading, setDeactivationLoading] = useState(false);

  // Fetch users on mount and when pagination changes
  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.page_size]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.page_size,
      };

      // Add filters
      if (filters.searchTerm) params.search = filters.searchTerm;
      if (filters.organization) params.organization = filters.organization;
      if (filters.status) params.is_active = filters.status;
      if (filters.sortBy) params.ordering = mapSortToOrdering(filters.sortBy);

      const response = await superAdminServices.getAllUsers(authTokens.access, params);

      setUsers(response.results || []);
      setPagination(prev => ({
        ...prev,
        count: response.count,
        total_pages: response.total_pages
      }));
    } catch (err) {
      setError(err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const mapSortToOrdering = (sortBy) => {
    const sortMap = {
      'email': 'email',
      'name': 'first_name',
      'organization': 'organisation__organisation_name',
      'newest': '-created_at',
      'oldest': 'created_at'
    };
    return sortMap[sortBy] || '-created_at';
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    fetchUsers();
  };

  const handleClearFilters = () => {
    setFilters({
      searchTerm: '',
      organization: '',
      status: '',
      sortBy: 'newest'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    // Fetch will be triggered by useEffect when filters change
    setTimeout(() => fetchUsers(), 0);
  };

  const handleShowFilters = () => {
    setShowFiltersSection(!showFiltersSection);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ 
      ...prev, 
      page_size: newPageSize, 
      page: 1 // Reset to first page when changing page size
    }));
  };

  const handleViewUser = (userId) => {
    navigate(`/admin-panel/users/${userId}`);
  };



  const handleDeactivateUser = (user) => {
    setSelectedUser(user);
    setDeactivationReason('');
    setShowDeactivationModal(true);
  };

  const handleCloseDeactivationModal = () => {
    setShowDeactivationModal(false);
    setSelectedUser(null);
    setDeactivationReason('');
    setDeactivationLoading(false);
  };

  const handleConfirmDeactivation = async () => {
    if (!selectedUser) return;

    setDeactivationLoading(true);
    
    try {
      const userData = {
        user_id: selectedUser.id,
        is_active: !selectedUser.is_active, // Toggle the current status
        reason: deactivationReason.trim() || undefined
      };

      await superAdminServices.deactivateUser(authTokens.access, userData);
      
      // Show success message (you can add toast notification here)
      console.log(`User ${selectedUser.is_active ? 'deactivated' : 'reactivated'} successfully`);
      
      // Refresh the users list
      await fetchUsers();
      
      // Close modal
      handleCloseDeactivationModal();
      
    } catch (err) {
      setError(err.message);
      setDeactivationLoading(false);
    }
  };

  const getRolesBadges = (roles) => {
    if (!roles || roles.length === 0) return <span className="text-xs text-gray-400">Aucun rôle</span>;
    
    return (
      <div className="flex flex-wrap gap-1">
        {roles.slice(0, 2).map((role) => (
          <span
            key={role.id}
            className="inline-block px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700"
          >
            {role.name}
          </span>
        ))}
        {roles.length > 2 && (
          <span className="text-xs text-gray-500">+{roles.length - 2}</span>
        )}
      </div>
    );
  };

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Tous les utilisateurs</h2>
          <p className="text-xs text-gray-500 mt-1">Gérez tous les utilisateurs du système</p>
        </div>
        
        <button
          onClick={handleShowFilters}
          className="inline-flex items-center px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          <Filter className="h-4 w-4 mr-2" />
          Filtres
          <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${showFiltersSection ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Filters Section */}
      {showFiltersSection && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Rechercher
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Email, nom, prénom..."
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Organization */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Organisation
              </label>
              <input
                type="text"
                placeholder="Nom de l'organisation"
                value={filters.organization}
                onChange={(e) => handleFilterChange('organization', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Statut
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Tous les statuts</option>
                <option value="true">Actif</option>
                <option value="false">Inactif</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Trier par
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="newest">Plus récent</option>
                <option value="oldest">Plus ancien</option>
                <option value="email">Email (A-Z)</option>
                <option value="name">Nom (A-Z)</option>
                <option value="organization">Organisation (A-Z)</option>
              </select>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex items-center justify-end space-x-3 mt-4">
            <button
              onClick={handleClearFilters}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              <X className="h-4 w-4 mr-1" />
              Effacer
            </button>
            <button
              onClick={handleApplyFilters}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Appliquer les filtres
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Utilisateur</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Organisation</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Rôles</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Créé le</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                      <span className="ml-2 text-sm text-gray-600">Chargement...</span>
                    </div>
                  </td>
                </tr>
              ) : users.length > 0 ? (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-green-600 rounded-full flex items-center justify-center mr-3">
                          <span className="text-xs font-semibold text-white">
                            {user.first_name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-900">
                            {user.first_name && user.last_name 
                              ? `${user.first_name} ${user.last_name}`
                              : 'Nom non renseigné'
                            }
                          </p>
                          {user.contact && (
                            <p className="text-xs text-gray-500">{user.contact}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-900">{user.email}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <Building2 className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-900">
                          {user.organization?.name || 'Aucune organisation'}
                        </span>
                        {user.organization?.is_subscribed && (
                          <span className="ml-2 inline-block w-2 h-2 bg-green-400 rounded-full" title="Abonné"></span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getRolesBadges(user.roles)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          user.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {user.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {new Date(user.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <button
                          onClick={() => handleViewUser(user.id)}
                          className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </button>

                        
                        <button
                          onClick={() => handleDeactivateUser(user)}
                          className={`inline-flex items-center justify-center p-1.5 rounded transition-colors ${
                            user.is_active
                              ? 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                              : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                          }`}
                          title={user.is_active ? 'Désactiver l\'utilisateur' : 'Réactiver l\'utilisateur'}
                        >
                          {user.is_active ? (
                            <UserX className="h-4 w-4" />
                          ) : (
                            <UserCheck className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center">
                      <Users className="h-12 w-12 text-gray-300 mb-3" />
                      <p className="text-xs text-gray-500">Aucun utilisateur trouvé</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          totalItems={pagination.count}
          pageSize={pagination.page_size}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
          itemName="utilisateurs"
        />
      </div>

      {/* Deactivation Confirmation Modal */}
      {showDeactivationModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <div className={`p-2 rounded-full mr-3 ${
                selectedUser.is_active ? 'bg-red-100' : 'bg-green-100'
              }`}>
                {selectedUser.is_active ? (
                  <UserX className="h-5 w-5 text-red-600" />
                ) : (
                  <UserCheck className="h-5 w-5 text-green-600" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedUser.is_active ? 'Désactiver' : 'Réactiver'} l'utilisateur
                </h3>
                <p className="text-sm text-gray-600">
                  {selectedUser.first_name && selectedUser.last_name 
                    ? `${selectedUser.first_name} ${selectedUser.last_name}`
                    : selectedUser.email
                  }
                </p>
              </div>
            </div>

            <div className="mb-4">
              <div className={`p-3 rounded-lg border ${
                selectedUser.is_active 
                  ? 'bg-red-50 border-red-200' 
                  : 'bg-green-50 border-green-200'
              }`}>
                <div className="flex items-start">
                  <AlertTriangle className={`h-4 w-4 mt-0.5 mr-2 ${
                    selectedUser.is_active ? 'text-red-600' : 'text-green-600'
                  }`} />
                  <div className="text-sm">
                    <p className={`font-medium ${
                      selectedUser.is_active ? 'text-red-800' : 'text-green-800'
                    }`}>
                      {selectedUser.is_active 
                        ? 'Êtes-vous sûr de vouloir désactiver cet utilisateur ?'
                        : 'Êtes-vous sûr de vouloir réactiver cet utilisateur ?'
                      }
                    </p>
                    <p className={`mt-1 ${
                      selectedUser.is_active ? 'text-red-700' : 'text-green-700'
                    }`}>
                      {selectedUser.is_active 
                        ? 'L\'utilisateur ne pourra plus se connecter à son compte.'
                        : 'L\'utilisateur pourra à nouveau se connecter à son compte.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Raison {selectedUser.is_active ? 'de la désactivation' : 'de la réactivation'} (optionnel)
              </label>
              <textarea
                value={deactivationReason}
                onChange={(e) => setDeactivationReason(e.target.value)}
                placeholder={`Expliquez pourquoi vous ${selectedUser.is_active ? 'désactivez' : 'réactivez'} cet utilisateur...`}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={3}
                disabled={deactivationLoading}
              />
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={handleCloseDeactivationModal}
                disabled={deactivationLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmDeactivation}
                disabled={deactivationLoading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center ${
                  selectedUser.is_active
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {deactivationLoading && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {selectedUser.is_active ? 'Désactiver' : 'Réactiver'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
